import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, Transition } from "@headlessui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSendCartOffer } from "app/hooks/checkout/useSendCartOffer";
import { useCartContext } from "app/providers/CartProvider";
import { Fragment, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import Checkbox from "shared/ui/checkbox";
import Icon, { IconTypes } from "shared/ui/icon";
import { decryptVoucher } from "shared/utils/voucher";
import { TextInputFormFields } from "widgets/shared/TextInputFormFields";
import { RequestStateIcons } from "widgets/shared/add-voucher-modal/components/RequestStateIcons";
import { z } from "zod";
import TextInput from "./components/Textinput";
import "./inputRevert.scss";
import InternalNotes from "features/shared/internal-notes";
import Text from "shared/ui/typography/Text";

const validationSchema = z.object({
  Voornaam: z.string().min(1, { message: "Voornaam is verplicht" }),
  Achternaam: z.string().min(1, { message: "Achternaam is verplicht" }),
  "E-mailadres": z.string().email({ message: "Ongeldig e-mailadres" }),
  Telefoonnummer: z
    .string()
    .optional()
    .refine((value) => (value ? isValidPhoneNumber(value, "NL") : true), {
      message: "Ongeldig telefoonnummer",
    }),
  newsletterConsent: z.boolean().optional(),
});

export default function SendCartOfferModal() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<TextInputFormFields>({
    resolver: zodResolver(validationSchema),
  });
  const cart = useCartContext().getCart();

  const { sendCartOfferInEmail, status, resetSendCartOffer } = useSendCartOffer(
    { setIsOpen, reset },
  );
  const isCartOfferSendingLoading = status === "loading";
  const onSubmit: SubmitHandler<TextInputFormFields> = (data) => {
    // Decrypt the discount code back to original string
    const newCart = { ...cart };
    newCart.email = data["E-mailadres"];
    newCart.emailAddress = data["E-mailadres"];
    newCart.address.shippingAddress.firstName = data.Voornaam;
    newCart.address.shippingAddress.lastName = data.Achternaam;
    newCart.address.shippingAddress.phone = data.Telefoonnummer;
    newCart.address.shippingAddress.email = data["E-mailadres"];
    newCart.address.invoiceAddress = { ...cart.address.shippingAddress };
    newCart.address.emailAddress = data["E-mailadres"];
    newCart.subscribeToNewsletter = data.newsletterConsent;
    newCart.discountCode = decryptVoucher(cart.discountCode);

    sendCartOfferInEmail(newCart);
  };

  const onModalClose = () => {
    setIsOpen(false);
    resetSendCartOffer();
  };
  return (
    <>
      <button onClick={() => setIsOpen(true)}>
        <h5 className="gg-flex hover:gg-text-yellow-400 hover:gg-underline">
          <Icon name={IconTypes.OFFER} className="gg-mr-2.5" />
          Stuur winkelwagen als offerte
        </h5>
      </button>
      <Transition as={Dialog} show={isOpen} onClose={onModalClose}>
        <Transition.Child
          as={Fragment}
          enter="gg-ease-out gg-duration-300"
          enterFrom="gg-opacity-0 gg-scale-95"
          enterTo="gg-opacity-100 gg-scale-100"
          leave="gg-ease-in gg-duration-100"
          leaveFrom="gg-opacity-100 gg-scale-100"
          leaveTo="gg-opacity-0 gg-scale-95"
        >
          <div className="send-cart-offer-modal gg-fixed gg-left-0 gg-top-0 gg-z-50 gg-grid gg-h-full gg-w-screen gg-place-items-center gg-overflow-x-hidden gg-backdrop-blur-md lg:gg-inset-0 lg:gg-h-auto">
            <Dialog.Panel className="gg-grid gg-h-fit gg-w-full gg-grid-cols-9 gg-rounded gg-bg-white gg-shadow-lg md:gg-max-w-[45rem]">
              {/* Header */}
              <section className="gg-relative gg-col-span-9 gg-flex gg-flex-col gg-px-5 gg-pt-9 gg-text-slate-800 sm:gg-px-8">
                {/* Close button */}
                <button
                  onClick={onModalClose}
                  className="mg:gg-top-5 gg-absolute gg-right-3 gg-top-3.5 gg-grid gg-h-5 gg-w-5 gg-cursor-pointer gg-place-items-center gg-rounded-full gg-border-none gg-bg-gray-200 gg-text-zinc-400 hover:gg-bg-gray-300 hover:gg-text-zinc-900 md:gg-right-5 lg:gg-right-6 lg:gg-top-6"
                >
                  <FontAwesomeIcon icon={faClose} size="sm" />
                </button>
                {/* Close button */}
                <h1 className="gg-w-full gg-text-xl gg-font-bold">
                  Klaar met ontwerpen? Stuur hem door via e-mail
                </h1>
                <p className="gg-mt-4 gg-w-full gg-text-sm">
                  Zo kun je jouw gemaakte ontwerp makkelijk terugvinden óf delen
                  met anderen.
                </p>
              </section>

              {/* Inputs */}

              <form
                id="sendCartOfferForm"
                onSubmit={handleSubmit(onSubmit)}
                className="gg-col-span-9 gg-mt-5 gg-grid gg-gap-x-10 gg-gap-y-5 gg-px-5 sm:gg-grid-cols-2 sm:gg-px-8"
              >
                <TextInput
                  register={register}
                  id="first_name"
                  label="Voornaam"
                  ariaLabel="Voornaam"
                  errors={errors}
                  disabled={isCartOfferSendingLoading}
                  className="lg:gg-max-w-xs"
                  inputClassName="gg-min-h-[2.5rem]"
                  required
                />

                <TextInput
                  register={register}
                  id="last_name"
                  label="Achternaam"
                  ariaLabel="Achternaam"
                  errors={errors}
                  disabled={isCartOfferSendingLoading}
                  className="lg:gg-max-w-xs"
                  inputClassName="gg-min-h-[2.5rem]"
                  required
                />

                <TextInput
                  register={register}
                  id="email"
                  label="E-mailadres"
                  ariaLabel="E-mailadres"
                  errors={errors}
                  disabled={isCartOfferSendingLoading}
                  className="lg:gg-max-w-xs"
                  inputClassName="gg-min-h-[2.5rem]"
                  required
                />

                <TextInput
                  register={register}
                  id="phone"
                  label="Telefoonnummer"
                  ariaLabel="Telefoonnummer"
                  errors={errors}
                  disabled={isCartOfferSendingLoading}
                  className="lg:gg-max-w-xs"
                  inputClassName="gg-min-h-[2.5rem]"
                />
              </form>
              {/* Inputs end */}

              {/* Checkmark newsletter */}
              <div className="gg-col-span-9 gg-flex gg-flex-row gg-items-center gg-gap-x-2.5 gg-px-5 gg-pt-5 sm:gg-px-8">
                <Controller
                  control={control}
                  name="newsletterConsent"
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <Checkbox
                      id="newsletterConsent"
                      checked={value !== undefined ? value : false}
                      className="gg-shrink-0"
                      onChange={onChange}
                    />
                  )}
                />

                <label
                  htmlFor="newsletterConsent"
                  className="gg-mt-1 gg-inline-flex gg-cursor-pointer gg-text-base gg-leading-6 gg-text-slate-800"
                >
                  Ik wil de nieuwsbrief ontvangen van
                  <span className="gg-ml-1 gg-font-bold gg-text-dark-blue">
                    GewoonGers
                  </span>
                </label>
              </div>

              <div className="gg-col-span-9 gg-items-center gg-gap-x-2.5 gg-px-5 sm:gg-px-8">
                <InternalNotes className="gg-pr-5 sm:gg-pr-8" />
              </div>

              {/* Fields warning message  */}
              <div className="gg-col-span-9 gg-flex gg-flex-row gg-items-center gg-gap-x-2.5 gg-px-5 gg-pb-5 sm:gg-px-8">
                <Text className="gg-text-sm gg-text-dark-blue-500">
                  Velden met een * zijn verplicht
                </Text>
              </div>

              {/* Checkmark newsletter */}
              {/* Mijn gers */}
              {/* <section className=" gg-pt-6 gg-col-span-9 gg-w-full ">
                <div className="gg-bg-gray-100 gg-gap-x-6 gg-grid gg-grid-cols-6 ">
                  <article className=" gg-col-span-6 sm:gg-col-span-4 gg-px-5 sm:gg-pl-8 gg-pt-5 gg-pb-7 sm:gg-py-7 gg-flex gg-flex-row  ">
                    <header className="gg-flex gg-flex-col  gg-text-slate-800">
                      <h1 className="gg-text-lg gg-font-bold gg-leading-5">
                        Maak een Mijn Gers account aan en sla ‘m op!
                      </h1>
                      <p className="gg-mt-2.5 sm:gg-mt-5 gg-text-base gg-leading-6">
                        En hou zo al jouw al jouw ontwerpen bij de hand, volg je
                        bestelling, hou de maten van jouw huis bij en veel meer.
                      </p>
                    
                      <h5 className="gg-uppercase gg-cursor-pointer gg-flex gg-items-center gg-gap-x-3 gg-underline gg-underline-offset-8 gg-mt-7 gg-text-sm gg-font-extrabold gg-tracking-wide">
                        binnenkort beschikbaar
                      </h5>
                    </header>
                  </article>

                  <div className="-gg-order-1 sm:gg-order-last  gg-pt-7 gg-pb-4 gg-pl-5 sm:gg-pl-0 sm:gg-pr-8 gg-col-span-6 sm:gg-col-span-2 md:gg-justify-self-center ">
                    <MijnGersIcons />
                  </div>
                </div>
              </section> */}
              {/* Mijn gers */}
              {/* Button */}
              <div className="gg-col-span-9 gg-flex gg-justify-end gg-border-t gg-px-4 gg-py-3 sm:gg-px-5 sm:gg-py-5">
                <button
                  type="submit"
                  disabled={isCartOfferSendingLoading}
                  form="sendCartOfferForm"
                  className="gg-flex gg-flex-row gg-items-center gg-gap-3.5 gg-whitespace-nowrap gg-rounded-sm gg-border-none gg-bg-yellow-300 gg-px-5 gg-py-2.5 gg-text-lg gg-font-bold gg-text-slate-800 gg-transition-colors gg-duration-300 gg-ease-in-out hover:gg-cursor-pointer hover:gg-bg-yellow-400 disabled:gg-bg-gray-200 sm:gg-px-6"
                >
                  Verstuur
                  <span className="gg-w-5">
                    <RequestStateIcons status={status} />
                  </span>
                </button>
              </div>
              {/* Button */}
            </Dialog.Panel>
          </div>
        </Transition.Child>
      </Transition>
    </>
  );
}
